/**
 * The record's own save bar (frame `65:2`): appears once something is
 * dirty, names how many fields changed, and carries the save and discard
 * actions. Never a toast (nothing here is transient -- the unsaved state
 * persists until the tenant acts on it) and never the page `<footer>`
 * (this bar belongs to the form, not to the page chrome).
 */
import type { ReactNode } from 'react';

import type { ApiFailure } from '../../../lib/http/errors';
import { ErrorState } from '../../../chrome/state/ErrorState';
import { st } from '../../../chrome/state/messages';
import { Button } from '../../../ui/button';
import { Spinner } from '../../../ui/spinner';
import { rm, rmPlural } from './recordMessages';

export function SaveBar(props: {
  changedCount: number;
  pending: boolean;
  failure: ApiFailure | null;
  onSave: () => void;
  onDiscard: () => void;
}): ReactNode {
  if (props.changedCount === 0) return null;

  return (
    <div
      role="region"
      aria-label={rm('saveBar.label')}
      className="bg-card sticky bottom-4 z-10 flex flex-col gap-3 rounded-lg border p-4 shadow-lg"
    >
      {props.failure !== null && <ErrorState failure={props.failure} />}
      <div className="flex items-center justify-between gap-4">
        <span className="text-body-sm">
          {rmPlural('saveBar.changed.other', props.changedCount, { count: props.changedCount })}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={props.pending}
            onClick={props.onDiscard}
          >
            {rm('saveBar.discard')}
          </Button>
          <Button
            type="button"
            disabled={props.pending}
            // The spinner's own `role="status"` name would otherwise be read
            // into this button's name-from-content ("CarregandoSalvar"); an
            // explicit label keeps it just the label while the spinner still
            // announces on its own.
            aria-label={rm('saveBar.save')}
            onClick={props.onSave}
          >
            {props.pending && <Spinner aria-label={st('loading')} />}
            {rm('saveBar.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
